import React from 'react';
import { trackEvent } from '../lib/analytics';
import { captureException } from '../lib/sentry';

// Top-level error boundary. Catches render-time exceptions in any
// descendant and shows a friendly "something went wrong" page instead
// of React's default white-screen unmount.
//
// Reporting (two complementary sinks — they fire in parallel):
//   • Sentry — full stack trace, breadcrumbs, browser/device context.
//              Only active when REACT_APP_SENTRY_DSN is set; otherwise
//              no-ops. See src/lib/sentry.js for setup.
//   • GA4   — `exception` event, consent-gated. Cheap volume signal so
//              we can see error rates per page in the same dashboard
//              where we already track funnel events.
// Sentry tells you WHAT broke and WHERE; GA4 tells you HOW OFTEN it
// breaks across real users. Both are useful, neither replaces the other.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Always log to console — useful for local dev and visible to anyone
    // debugging via DevTools regardless of Sentry/GA4 configuration.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);

    // Sentry: full fidelity (stack, breadcrumbs, browser context).
    // No-op when REACT_APP_SENTRY_DSN is unset — see src/lib/sentry.js.
    captureException(error, {
      componentStack: info && info.componentStack,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });

    // GA4: free aggregate counter. Standard `exception` event schema so
    // it shows up in Reports → Events view without any custom config.
    trackEvent('exception', {
      description: (error && error.message) || 'Unknown error',
      fatal: true,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'inherit',
        }}>
          <h2 style={{ fontSize: '1.5rem', color: '#0d1b2a', marginBottom: '0.75rem' }}>
            Something went wrong
          </h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Sorry, an unexpected error occurred. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 2rem',
              background: '#BEAA66',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 700,
              fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
